import { badgeOf } from '../core/badge';
import { CAMERA_LABELS, formatDate, formatTime, momentLabel, placeLabel } from '../core/format';
import { CHIPS, countChips, filterTriage, sectionOf, sortTriage, SORT_ORDERS, type Chip, type EventEntry, type SortOrder, type TriageItem } from '../core/triage';
import { SORT_TITLES } from './commands';
import type { Moment } from '../core/moments';
import type { LibraryFile } from './library';

const CHIP_LABELS: Readonly<Record<Chip, string>> = {
  all: 'All',
  incidents: 'Incidents',
  driving: 'Driving',
  saved: 'Saved',
  sentry: 'Sentry',
  clips: 'Clips',
};
const MOMENTS_SHOWN = 2;
const SORT_STORAGE_KEY = 'tesla-videoplayer.sort';
const TRASH_ICON = '<svg class="tv-icon tv-icon--small"><use href="#tv-icon-trash"/></svg>';
const CHEVRON_ICON = '<svg class="tv-icon tv-icon--small"><use href="#tv-icon-chevron"/></svg>';

export interface ListDom {
  readonly list: HTMLOListElement;
  readonly chips: HTMLElement;
  readonly search: HTMLInputElement;
  readonly sort: HTMLSelectElement;
  readonly empty: HTMLElement;
}

export interface ListActions {
  readonly select: (footageId: string) => void;
  readonly remove: (footageId: string) => void;
}

type Entry = EventEntry<LibraryFile>;
type Item = TriageItem<LibraryFile>;

const element = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const isSortOrder = (value: string | null): value is SortOrder => SORT_ORDERS.some((order) => order === value);

function storedSort(): SortOrder {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return isSortOrder(stored) ? stored : 'rank';
  } catch {
    return 'rank';
  }
}

function storeSort(order: SortOrder): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, order);
  } catch {
    return;
  }
}

const titleOf = (entry: Entry): string => entry.info?.reason.label ?? 'Recording';

function detailOf(entry: Entry): string {
  const camera = entry.info?.camera;
  const side = camera && entry.sentry ? ` · ${CAMERA_LABELS[camera]} camera` : '';
  return `${formatDate(entry.time)} · ${formatTime(entry.time).slice(0, 5)}${side}`;
}

const leadOf = (item: Item): Entry => (item.kind === 'session' ? item.lead : item.entry);
const entriesIn = (item: Item): readonly Entry[] => (item.kind === 'session' ? item.entries : [item.entry]);

export class EventList {
  private items: readonly Item[] = [];
  private chip: Chip = 'all';
  private query = '';
  private order: SortOrder = storedSort();
  private selected: string | undefined;
  private deletable = false;
  private readonly expanded = new Set<string>();
  private readonly thumbs = new Map<string, string>();
  private readonly onSearch = (): void => {
    this.query = this.dom.search.value;
    this.render();
  };
  private readonly onSort = (): void => {
    if (isSortOrder(this.dom.sort.value)) this.setSort(this.dom.sort.value);
  };

  constructor(
    private readonly dom: ListDom,
    private readonly actions: ListActions,
  ) {
    dom.search.addEventListener('input', this.onSearch);
    dom.sort.addEventListener('change', this.onSort);
    dom.sort.replaceChildren(...SORT_ORDERS.map((order) => Object.assign(document.createElement('option'), { value: order, textContent: SORT_TITLES[order] })));
    dom.sort.value = this.order;
  }

  update(items: readonly Item[]): void {
    this.items = items;
    this.render();
  }

  setDeletable(deletable: boolean): void {
    this.deletable = deletable;
    this.render();
  }

  setChip(chip: Chip): void {
    this.chip = chip;
    this.render();
  }

  setSort(order: SortOrder): void {
    this.order = order;
    this.dom.sort.value = order;
    storeSort(order);
    this.render();
  }

  cycleSort(): SortOrder {
    const next = SORT_ORDERS[(SORT_ORDERS.indexOf(this.order) + 1) % SORT_ORDERS.length]!;
    this.setSort(next);
    return next;
  }

  select(footageId: string | undefined): void {
    this.selected = footageId;
    const session = this.sessionOf(footageId);
    if (session && session.kind === 'session' && session.lead.id !== footageId) this.expanded.add(session.id);
    this.render();
    this.dom.list.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
  }

  toggleSession(footageId: string | undefined): void {
    const session = this.sessionOf(footageId);
    if (!session) return;
    if (this.expanded.has(session.id)) this.expanded.delete(session.id);
    else this.expanded.add(session.id);
    this.render();
  }

  neighbour(direction: 1 | -1, from = this.selected): string | undefined {
    const order = this.visible().flatMap((item) => (item.kind === 'session' && this.expanded.has(item.id) ? item.entries.map((entry) => entry.id) : [leadOf(item).id]));
    const index = from === undefined ? -1 : order.indexOf(from);
    return order[index < 0 ? 0 : index + direction];
  }

  entryById(footageId: string | undefined): Entry | undefined {
    return this.items.flatMap(entriesIn).find((entry) => entry.id === footageId);
  }

  destroy(): void {
    this.dom.search.removeEventListener('input', this.onSearch);
    this.dom.sort.removeEventListener('change', this.onSort);
    this.thumbs.forEach((url) => URL.revokeObjectURL(url));
    this.thumbs.clear();
    this.dom.list.replaceChildren();
    this.dom.chips.replaceChildren();
  }

  private sessionOf(footageId: string | undefined): Item | undefined {
    return this.items.find((item) => item.kind === 'session' && item.entries.some((entry) => entry.id === footageId));
  }

  private visible(): readonly Item[] {
    return sortTriage(filterTriage(this.items, this.chip, this.query), this.order);
  }

  private render(): void {
    this.renderChips();
    const visible = this.visible();
    let section: string | undefined;
    const rows = visible.flatMap((item) => {
      const heading = sectionOf(item, this.order);
      const head = heading !== undefined && heading !== section ? [element('li', 'tv-section', heading)] : [];
      section = heading;
      return [...head, this.renderItem(item)];
    });
    this.dom.list.replaceChildren(...rows);
    this.dom.empty.hidden = visible.length > 0 || this.items.length === 0;
  }

  private renderChips(): void {
    const counts = countChips(this.items);
    const chips = CHIPS.filter((chip) => chip === 'all' || counts[chip] > 0).map((chip) => {
      const button = element('button', 'tv-chip', `${CHIP_LABELS[chip]} `);
      button.type = 'button';
      button.setAttribute('aria-pressed', String(chip === this.chip));
      button.append(element('span', 'tv-chip-count', String(counts[chip])));
      button.addEventListener('click', () => this.setChip(chip));
      return button;
    });
    this.dom.chips.replaceChildren(...chips);
  }

  private renderItem(item: Item): HTMLLIElement {
    if (item.kind === 'event') return this.renderEntry(item.entry);
    const row = this.renderEntry(item.lead, item.entries);
    const open = this.expanded.has(item.id);
    const toggle = element('button', 'tv-item-expand');
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', `${open ? 'Hide' : 'Show'} all ${item.entries.length} events of this parking session (X)`);
    toggle.innerHTML = CHEVRON_ICON;
    toggle.addEventListener('click', () => this.toggleSession(item.lead.id));
    row.append(toggle);
    if (open) {
      const nested = element('ol', 'tv-sublist');
      nested.append(...[...item.entries].sort((a, b) => a.time - b.time).map((entry) => this.renderEntry(entry)));
      row.append(nested);
    }
    return row;
  }

  private renderEntry(entry: Entry, session?: readonly Entry[]): HTMLLIElement {
    const sessionSize = session?.length;
    const row = element('li', 'tv-item');
    const main = element('button', 'tv-item-main');
    main.type = 'button';
    if (entry.id === this.selected) main.setAttribute('aria-current', 'true');
    main.addEventListener('click', () => this.actions.select(entry.id));
    main.append(this.thumbnail(entry, (session ?? [entry]).some((member) => member.notes !== undefined)), this.body(entry, sessionSize));
    row.append(main);
    if (this.deletable && sessionSize === undefined) row.append(this.trash(entry));
    return row;
  }

  private trash(entry: Entry): HTMLButtonElement {
    const button = element('button', 'tv-item-delete');
    button.type = 'button';
    button.setAttribute('aria-label', `Delete ${titleOf(entry)} at ${placeLabel(entry.info)} (⌫)`);
    button.innerHTML = TRASH_ICON;
    button.addEventListener('click', () => this.actions.remove(entry.id));
    return button;
  }

  private body(entry: Entry, sessionSize: number | undefined): HTMLElement {
    const body = element('span', 'tv-item-body');
    const top = element('span', 'tv-item-top');
    const { label, tone } = badgeOf(entry.info, entry.notes, entry.tier);
    const badge = element('span', 'tv-badge', label);
    badge.dataset['tone'] = tone;
    top.append(badge);
    if (sessionSize) top.append(element('span', 'tv-item-count', `${sessionSize} events`));
    body.append(top, element('span', 'tv-item-place', placeLabel(entry.info)), element('span', 'tv-item-time', detailOf(entry)));
    const summary = entry.notes?.summary;
    const moments = [...entry.moments].sort((a, b) => b.peak - a.peak).slice(0, MOMENTS_SHOWN);
    if (summary) body.append(element('span', 'tv-item-summary', summary));
    else if (moments.length) body.append(this.momentLine(moments));
    return body;
  }

  private momentLine(moments: readonly Moment[]): HTMLElement {
    const line = element('span', 'tv-item-moments');
    moments.forEach((moment, i) => {
      const label = element('span', 'tv-moment', momentLabel(moment));
      label.dataset['kind'] = moment.kind;
      line.append(...(i > 0 ? [' · ', label] : [label]));
    });
    return line;
  }

  private thumbnail(entry: Entry, noted: boolean): HTMLElement {
    const frame = element('span', 'tv-thumb-frame');
    frame.append(this.thumbImage(entry));
    if (noted) frame.append(Object.assign(element('span', 'tv-thumb-ai', 'AI'), { title: 'Has AI notes' }));
    return frame;
  }

  private thumbImage(entry: Entry): HTMLElement {
    const thumb = entry.footage.thumb;
    if (!thumb) return element('span', 'tv-thumb tv-thumb--empty');
    const image = element('img', 'tv-thumb');
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    const cached = this.thumbs.get(entry.id) ?? URL.createObjectURL(thumb.file);
    this.thumbs.set(entry.id, cached);
    image.src = cached;
    return image;
  }
}
